<template>
  <div
    class="vue-slider-container"
    ref="refVueSliderContainer"
    :style="renderStyleData"
    @click="handleSlideBoxClick"
    :class="{
      'vue-slider-container-disabled': props.disableChangeProgress,
      'vue-slider-container-active': renderData.touchSwipeState,
    }"
  >
    <div class="vue-slider-track">
      <div class="vue-slider-progress"></div>
      <div class="vue-slider-dot-progress">
        <div class="vue-slider-dot-op-box" ref="refProgressDotBox">
          <div class="vue-slider-dot"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  defineProps,
  defineEmits,
  computed,
  readonly,
  reactive,
  watch,
  defineExpose,
  onMounted,
  onUpdated,
  onUnmounted,
} from "vue"

import tools from "@/utils/tools.js"

import { useRouter, useRoute } from "vue-router"

const Router = useRouter()
const route = useRoute()

const props = defineProps({
  modelValue: {
    type: Number,
    default: 0,
  },
  tooltipFormatter: {
    type: Function,
    default: (str) => {
      return str
    },
  },
  min: {
    type: Number,
    default: 0,
  },
  max: {
    type: Number,
    default: 100,
  },
  renderStyle: {
    type: Object,
    default: () => ({}),
  },
  disableChangeProgress: {
    type: Boolean,
    default: false,
  },
})
const emits = defineEmits([
  "update:modelValue",
  "change",
  "dragStart",
  "dragEnd",
  "dragging",
])

const defaltRenderStyle = {
  '--slider-height': '16px',
  
  '--track-height': '2px',
  '--track-radius': '1px',
  '--track-bg-color': 'rgba(255, 255, 255, 0.2)',

  '--progress-bg-color': 'linear-gradient(135deg, #4fdeff 0%, #e675ff 100%)',
  '--dot-padding': '4px',

  '--slider-dot-size': '10px',
  '--dot-bg-color': 'linear-gradient(135deg, #4fdeff 0%, #e675ff 100%)',
  '--dot-hover-scale': '1.2',
}

const refVueSliderContainer = ref(null)
const refProgressDotBox = ref(null)

const renderStyleData = computed(() => {
  return {
    ...defaltRenderStyle,
    ...props.renderStyle,
    "--progress-width": renderData.touchSwipeState
      ? `${
          ((renderData.modelValue - props.min) / (props.max - props.min)) * 100
        }%`
      : `${((props.modelValue - props.min) / (props.max - props.min)) * 100}%`,
  }
})
const renderData = reactive({
  touchSwipeState: false,
  modelValue: props.modelValue,
})

const stateData = {
  registTouchWaveBarSwipeStartCall: null,
}

function doChangeProgress(val) {
  emits("update:value", val)
  emits("change", val)
}

const handleSlideBoxClick = (e) => {
  if (props.disableChangeProgress || renderData.touchSwipeState) {
    return
  }
  const { width, left } = refVueSliderContainer.value.getBoundingClientRect()
  const clientX = e.clientX
  const newVal = Math.floor(
    ((clientX - left) / width) * (props.max - props.min) + props.min
  )
  renderData.modelValue = newVal
  doChangeProgress(newVal)
}

const initEvent = () => {
  renderData.touchSwipeState = false
  if (props.disableChangeProgress) {
    return
  }
  let transLeft = 0
  let width = refVueSliderContainer.value?.clientWidth

  stateData.registTouchWaveBarSwipeStartCall = tools.registTouchSwipe(
    refProgressDotBox.value,
    {
      direction: "horizontal",
      startCb: (e, actionData) => {
        width = refVueSliderContainer.value?.clientWidth
        if (!renderData.touchSwipeState) {
          renderData.touchSwipeState = true
          renderData.modelValue = props.modelValue
          transLeft =
            ((renderData.modelValue - props.min) / (props.max - props.min)) *
            width
          emits("dragStart", renderData.modelValue)
        }
      },
      moveCb: (e, actionData) => {
        const { startX, endX } = actionData
        const dx = Math.floor(endX - startX)

        let newTransLeft = transLeft + dx
        newTransLeft = Math.max(0, Math.min(newTransLeft, width))
        renderData.modelValue = Math.floor(
          (newTransLeft / width) * (props.max - props.min) + props.min
        )
        emits("dragging", renderData.modelValue)
      },
      endCb: (e, actionData) => {
        if (renderData.touchSwipeState) {
          setTimeout(() => {
            renderData.touchSwipeState = false
            emits("dragEnd", renderData.modelValue)
          }, 10)
          doChangeProgress(renderData.modelValue)
        }
      },
    }
  )
}
watch(
  () => props.modelValue,
  (val) => {
    if (!renderData.touchSwipeState) {
      renderData.modelValue = val
    }
  }
)
onMounted(() => {
  if (refVueSliderContainer.value) {
    initEvent()
  }
})
</script>
<style lang="scss">
.vue-slider-container {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  height: var(--slider-height);
  .vue-slider-track {
    width: 100%;
    height: var(--track-height);
    border-radius: var(--track-radius);
    display: flex;
    background: var(--track-bg-color);
    position: relative;
  }
  .vue-slider-progress {
    height: 100%;
    background: var(--progress-bg-color);
    width: var(--progress-width);
    // transition: width 0.2s ease-in-out;
  }
  .vue-slider-dot-progress {
    width: 0;
    height: 0;
    position: absolute;
    left: var(--progress-width);
    top: 50%;
    // transition: left 0.2s ease-in-out;
  }
  .vue-slider-dot-op-box {
    padding: var(--dot-padding);
    border-radius: 50%;
    position: absolute;
    left: 0;
    top: 0;
    transform: translate(-50%, -50%);

    cursor: pointer;
    .vue-slider-dot {
      width: var(--slider-dot-size);
      height: var(--slider-dot-size);
      background: var(--dot-bg-color);
      border-radius: 50%;
    }
    &:hover {
      .vue-slider-dot {
        transform: scale(var(--dot-hover-scale));
      }
    }
  }
  &.vue-slider-container-active {
    .vue-slider-progress {
      transition: unset;
    }
    .vue-slider-dot-progress{
      transition: unset;
    }
  }
}
</style>
